/**
 * useEmbeddingConfig Hook
 * Standardized API hooks for embedding configuration management
 *
 * Refactored: Phase 3.5 - Factory Pattern Migration
 * Pattern: Uses createQueryHookNoParams and createMutationHook factories for code reduction
 * Previous: Manual useQuery/useMutation with createApiClient calls
 * Current: Factory-based hooks with built-in logging and invalidation
 *
 * Migration Benefits:
 * - Reduced code duplication
 * - Standardized logging patterns
 * - Centralized query invalidation
 * - Type-safe configuration
 */

import {
  EmbeddingConfigRequest,
  EmbeddingConfigResponse,
  EmbeddingProviderResponse,
  StatusResponse,
} from '@/api/data-contracts';
import { log } from '@/utils/errors/logger';
import { QUERY_STALE_TIME } from '@/constants/queryConfig';
import { createQueryHookNoParams, createMutationHook } from '@/hooks/api/factories';
import { usePermissions } from '@/hooks/permissions/usePermissions';

// ============================================================================
// Query Keys
// ============================================================================

export const embeddingConfigKeys = {
  all: ['embeddingConfigs'] as const,
  configs: () => [...embeddingConfigKeys.all, 'configs'] as const,
  current: () => [...embeddingConfigKeys.all, 'current'] as const,
  providers: () => [...embeddingConfigKeys.all, 'providers'] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Base hook to get all embedding configurations
 * Uses factory pattern with automatic API client injection
 */
const useGetEmbeddingConfigsBase = createQueryHookNoParams<EmbeddingConfigResponse[]>({
  queryKey: () => embeddingConfigKeys.configs(),
  queryFn: (apiClient) =>
    apiClient.getAllConfigsApiAdminEmbeddingConfigAllGet().then(response => {
      log.api.response('GET', '/api/admin/embedding-config/all', 200, { count: response.data.length });
      return response.data;
    }),
  logContext: 'GET /api/admin/embedding-config/all',
  staleTime: QUERY_STALE_TIME.LONG, // Embedding configs change infrequently
});

/**
 * Hook to get all embedding configurations.
 * Only enabled for users with embedding configuration permission.
 *
 * Features:
 * - Permission-based access via enabled check
 * - Long stale time (5 minutes) as configs change infrequently
 * - Automatic request/response/error logging via logContext
 *
 * Usage:
 * @example
 * // Basic usage (auto-checks permissions)
 * const { data: configs } = useGetEmbeddingConfigs();
 */
export function useGetEmbeddingConfigs() {
  const { can } = usePermissions();
  return useGetEmbeddingConfigsBase({
    enabled: can.configureEmbedding
  });
}

/**
 * Base hook to get current active embedding configuration
 */
const useGetCurrentEmbeddingConfigBase = createQueryHookNoParams<EmbeddingConfigResponse>({
  queryKey: () => embeddingConfigKeys.current(),
  queryFn: (apiClient) =>
    apiClient.getCurrentConfigApiAdminEmbeddingConfigCurrentGet().then(response => {
      log.api.response('GET', '/api/admin/embedding-config/current', 200, { model: response.data.model });
      return response.data;
    }),
  logContext: 'GET /api/admin/embedding-config/current',
  staleTime: QUERY_STALE_TIME.MEDIUM, // Current config checked moderately often
});

/**
 * Hook to get current active embedding configuration.
 * Only enabled for users with embedding configuration permission.
 */
export function useGetCurrentEmbeddingConfig() {
  const { can } = usePermissions();
  return useGetCurrentEmbeddingConfigBase({
    enabled: can.configureEmbedding
  });
}

/**
 * Base hook to get available embedding providers
 */
const useGetEmbeddingProvidersBase = createQueryHookNoParams<EmbeddingProviderResponse[]>({
  queryKey: () => embeddingConfigKeys.providers(),
  queryFn: (apiClient) =>
    apiClient.getAvailableProvidersApiAdminEmbeddingConfigProvidersGet().then(response => {
      log.api.response('GET', '/api/admin/embedding-config/providers', 200, { count: response.data.length });
      return response.data;
    }),
  logContext: 'GET /api/admin/embedding-config/providers',
  staleTime: QUERY_STALE_TIME.VERY_LONG, // Providers rarely change
});

/**
 * Hook to get available embedding providers.
 * Only enabled for users with embedding configuration permission.
 */
export function useGetEmbeddingProviders() {
  const { can } = usePermissions();
  return useGetEmbeddingProvidersBase({
    enabled: can.configureEmbedding
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to test embedding configuration
 */
export const useTestEmbeddingConfig = createMutationHook<unknown, EmbeddingConfigRequest | EmbeddingConfigResponse>({
  mutationFn: (apiClient, config) =>
    apiClient.testConfigApiAdminEmbeddingConfigTestPost(config).then(response => {
      log.api.response('POST', '/api/admin/embedding-config/test', 200, {
        success: response.data.success,
        model: config.model
      });
      return response.data;
    }),
  logContext: 'POST /api/admin/embedding-config/test',
  logMethod: 'POST',
});

/**
 * Hook to save/update embedding configuration
 */
export const useSaveEmbeddingConfig = createMutationHook<StatusResponse, EmbeddingConfigRequest>({
  mutationFn: (apiClient, config) =>
    apiClient.saveConfigApiAdminEmbeddingConfigSavePost(config).then(response => {
      log.api.response('POST', '/api/admin/embedding-config/save', 200, { model: config.model });
      return response.data;
    }),
  invalidateQueries: [
    embeddingConfigKeys.configs(),
    embeddingConfigKeys.current(),
  ],
  logContext: 'POST /api/admin/embedding-config/save',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('embedding_config_saved', 'useEmbeddingConfig');
  },
});

/**
 * Hook to activate an existing embedding configuration
 */
export const useActivateEmbeddingConfig = createMutationHook<StatusResponse, string>({
  mutationFn: (apiClient, configId) =>
    apiClient.activateConfigApiAdminEmbeddingConfigConfigIdActivatePost(configId).then(response => {
      log.api.response('POST', `/api/admin/embedding-config/${configId}/activate`, 200, { configId });
      return response.data;
    }),
  invalidateQueries: [
    embeddingConfigKeys.configs(),
    embeddingConfigKeys.current(),
  ],
  logContext: 'POST /api/admin/embedding-config/:id/activate',
  logMethod: 'POST',
  onSuccess: (_data, configId) => {
    log.user.action('embedding_config_activated', 'useEmbeddingConfig', { configId });
  },
});

/**
 * Hook to delete embedding configuration
 */
export const useDeleteEmbeddingConfig = createMutationHook<StatusResponse, string>({
  mutationFn: (apiClient, configId) =>
    apiClient.deleteConfigApiAdminEmbeddingConfigConfigIdDelete(configId).then(response => {
      log.api.response('DELETE', `/api/admin/embedding-config/${configId}`, 200, { configId });
      return response.data;
    }),
  invalidateQueries: [
    embeddingConfigKeys.configs(),
    embeddingConfigKeys.current(),
  ],
  logContext: 'DELETE /api/admin/embedding-config/:id',
  logMethod: 'DELETE',
  onSuccess: (_data, configId) => {
    log.user.action('embedding_config_deleted', 'useEmbeddingConfig', { configId });
  },
});

/**
 * Hook to reset to environment configuration
 */
export const useResetToEnvEmbeddingConfig = createMutationHook<StatusResponse, void>({
  mutationFn: (apiClient) =>
    apiClient.resetToEnvApiAdminEmbeddingConfigResetToEnvPost().then(response => {
      log.api.response('POST', '/api/admin/embedding-config/reset-to-env', 200, {});
      return response.data;
    }),
  invalidateQueries: [
    embeddingConfigKeys.configs(),
    embeddingConfigKeys.current(),
  ],
  logContext: 'POST /api/admin/embedding-config/reset-to-env',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('embedding_config_reset_to_env', 'useEmbeddingConfig');
  },
});